import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, ArrowsLeftRight, CheckCircle, Clock } from "@phosphor-icons/react";
import { Chapter, Reveal } from "./Reveal";
import { useApkDownload } from "@/lib/useApkDownload";

function AppPreviewMockup() {
  return (
    <div className="relative mx-auto w-[300px] sm:w-[340px]" data-testid="app-preview-mockup">
      <div className="absolute -inset-12 rounded-full bg-brand-500/20 blur-3xl" aria-hidden="true" />
      <div className="relative rounded-[2.8rem] border border-line bg-pine p-3 shadow-[0_50px_100px_-20px_rgba(0,0,0,0.75)]">
        <div className="overflow-hidden rounded-[2.2rem] bg-night text-paper">
          <div className="flex items-center justify-between px-6 pb-2 pt-6">
            <div className="flex items-center gap-2.5">
              <img src="/assets/logo-icon.png" alt="" className="h-8 w-8 rounded-xl" />
              <span className="font-display text-base font-semibold tracking-tight">Rupora</span>
            </div>
            <span className="rounded-full bg-brand-500/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-brand-300">Live</span>
          </div>

          <div className="mx-6 mt-4 rounded-2xl bg-gradient-to-br from-brand-600 to-brand-800 p-5">
            <p className="text-[11px] uppercase tracking-widest text-white/60">Wallet Balance</p>
            <p className="font-display mt-1 text-3xl font-semibold tracking-tight text-white">₹24,500.00</p>
            <p className="mt-1 text-[11px] text-white/60">≈ 222.7 USDT</p>
          </div>

          <div className="mx-6 mt-4 flex items-center justify-between rounded-2xl border border-line bg-pine px-5 py-4">
            <span className="text-xs text-mist">Current USDT rate</span>
            <span className="font-display text-sm font-semibold text-brand-300">₹110.00</span>
          </div>

          <div className="mx-6 mt-4 rounded-2xl border border-line bg-pine p-4">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-mist">
              <ArrowsLeftRight size={13} weight="duotone" className="text-brand-400" /> Exchange
            </div>
            <div className="mt-3 flex items-center justify-between rounded-xl bg-night px-4 py-3">
              <span className="font-mono text-sm">100 USDT</span>
              <ArrowDown size={13} className="text-mist" />
            </div>
            <div className="mt-2 flex items-center justify-between rounded-xl bg-brand-500/10 px-4 py-3">
              <span className="font-mono text-sm font-semibold text-brand-300">₹11,000</span>
              <span className="text-[10px] text-mist">INR</span>
            </div>
          </div>

          <div className="px-6 pb-7 pt-5">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-mist">Recent transactions</p>
            <div className="mt-3 space-y-2.5">
              {[
                { icon: ArrowDown, t: "Deposit · 100 USDT", s: "Completed", ok: true },
                { icon: ArrowUp, t: "Withdrawal · ₹11,000", s: "Processing", ok: false },
              ].map((row) => (
                <div key={row.t} className="flex items-center justify-between text-xs">
                  <span className="inline-flex items-center gap-2.5 text-paper/75">
                    <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-pine">
                      <row.icon size={13} weight="bold" className="text-brand-400" />
                    </span>
                    {row.t}
                  </span>
                  <span className={`inline-flex items-center gap-1 ${row.ok ? "text-brand-300" : "text-amber-300"}`}>
                    {row.ok ? <CheckCircle size={12} weight="fill" /> : <Clock size={12} weight="fill" />}
                    {row.s}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AppPreview() {
  const { downloading, start } = useApkDownload();
  const [version, setVersion] = useState(null);

  useEffect(() => {
    fetch("/api/app-version")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d?.version && setVersion(d.version))
      .catch(() => {});
  }, []);

  return (
    <section id="app" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32" data-testid="app-preview-section">
      <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <Chapter index="06" label="The App" />
          <Reveal delay={0.1}>
            <h2 className="font-display text-4xl font-semibold tracking-tighter text-paper sm:text-5xl">
              Your exchange, <span className="text-brand-400">in your pocket.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-mist sm:text-lg">
              Wallet balance, live rates, exchange, transaction status and history — the full
              Rupora experience in a fast mobile app.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center">
              <button
                onClick={start}
                disabled={downloading}
                data-testid="download-app-button"
                className="group inline-flex items-center justify-center gap-3 rounded-full bg-gradient-to-r from-brand-500 to-brand-600 px-9 py-4 text-base font-semibold text-white shadow-xl shadow-brand-500/25 transition-all duration-300 hover:shadow-2xl hover:shadow-brand-500/40 hover:brightness-110 disabled:opacity-70"
              >
                <ArrowDown size={19} weight="bold" className="transition-transform duration-300 group-hover:translate-y-0.5" />
                {downloading ? "Downloading..." : "Download Rupora App"}
              </button>
              <span className="inline-flex items-center justify-center gap-2 text-sm text-mist" data-testid="app-version">
                {version ? `Latest version v${version} · ` : ""}Android · Free download
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.4}>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-mist">
              {["Secure download from this page", "Verified process", "Transaction tracking"].map((t) => (
                <span key={t} className="inline-flex items-center gap-2">
                  <CheckCircle size={15} weight="duotone" className="text-brand-400" /> {t}
                </span>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-6">
          <Reveal delay={0.2}>
            <AppPreviewMockup />
          </Reveal>
        </div>
      </div>
    </section>
  );
}
